import classNames from "classnames";
import {
  ForwardedRef,
  InputHTMLAttributes,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import TextInput from "components/ui/TextInput";
import useFieldFeedback from "./../useFieldFeedback";
import "./../InputTextElement.css";
import "./../../common.css";

export type InputHandle = {
  focus: () => void;
  blur: () => void;
  clearState: () => void;
  isInvalid: () => boolean;
};

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "ref" | "size"> & {
  formSubmitted?: boolean;
  invalid?: boolean;
  rtl?: boolean;
};

/**
 * A ui/TextInput that warns about itself: "* Required field" once an empty
 * required field has been visited or its form submitted, and a countdown as the
 * value nears `maxLength`.
 */
function Input(props: InputProps, ref: ForwardedRef<InputHandle>) {
  const { formSubmitted, invalid, rtl, className, ...rest } = props;
  const elementRef = useRef<HTMLInputElement>(null);
  const feedback = useFieldFeedback<HTMLInputElement>({ ...rest, formSubmitted });

  useImperativeHandle(ref, () => ({
    focus: () => elementRef.current?.focus(),
    blur: () => elementRef.current?.blur(),
    clearState: feedback.clearState,
    isInvalid: feedback.isInvalid,
  }));

  return (
    <div className="input-container">
      <TextInput
        {...rest}
        ref={elementRef}
        className={classNames(className, feedback.stateClass)}
        invalid={invalid || feedback.stateClass === "required"}
        dir={rtl ? "rtl" : undefined}
        {...feedback.handlers}
      />
      {feedback.tooltip && (
        <span className={classNames("input-tooltip", feedback.stateClass)}>
          {feedback.tooltip}
        </span>
      )}
    </div>
  );
}

export default forwardRef(Input);
